import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@pl/domain';
import type { WhatsAppDto, WhatsAppGroupDto } from '@pl/domain';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { zonedDateTime } from '../../lib/format';
import { qk, useMe, useOrg, useWhatsApp, useWhatsAppGroupChooser, useWhatsAppGroups } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import s from './SettingsWhatsApp.module.css';

/**
 * The header chip: only what the server said just now, and never more than it said. pallet-wa is
 * `unknown` from every start until its socket first reports, and for as long as it cannot start its
 * session: the phone is still linked then, so this must never read as "Not linked" (review I1).
 */
function status(w: WhatsAppDto): { tone?: 'ok' | 'warn' | 'bad'; label: string } {
  if (!w.setUp) return { label: 'Not set up' };
  if (!w.reachable) return { tone: 'bad', label: 'Not answering' };
  switch (w.state) {
    case 'connected':
      return { tone: 'ok', label: 'Connected' };
    case 'pairing':
      return { tone: 'warn', label: 'Waiting to be scanned' };
    case 'reconnecting':
      return { tone: 'warn', label: 'Reconnecting' };
    case 'logged_out':
      return { tone: 'warn', label: 'Not linked' };
    default:
      return { label: 'Starting up' };
  }
}

/**
 * Office → Settings → WhatsApp: the company's WhatsApp number, as pallet-wa holds it, and its group
 * alerts. Pair the number by scanning the QR here, choose the group alerts go to, and see what was
 * sent. Admins only; the API answers only for the company whose number pallet-wa holds.
 */
export function SettingsWhatsApp() {
  const meQuery = useMe();
  const me = meQuery.data;
  const canManage = me ? can(me.role, 'settings') : false;
  const waQuery = useWhatsApp(canManage);
  const timeZone = useOrg().data?.timezone;

  if (!me) return <Spinner label="Loading WhatsApp" />;
  if (!canManage)
    return (
      <div className="panel">
        <Empty title="Only an admin can set up WhatsApp." description="An admin links the company’s WhatsApp number here, and chooses the group alerts go to." />
      </div>
    );

  const wa = waQuery.data;
  const chip = wa ? status(wa) : null;
  return (
    <div className={s.page}>
      <div className="panel">
        <div className="panel-h">
          <h2>WhatsApp</h2>
          {chip && (
            <Chip tone={chip.tone} dot={Boolean(chip.tone)}>
              {chip.label}
            </Chip>
          )}
          <span className="grow" />
          <span className="sub">The company’s number, for the assistant and group alerts</span>
        </div>
        <RefetchNote queries={[waQuery]} className={s.note} />
        {waQuery.isLoadingError ? (
          <div className={s.note}>
            <ErrorNote error={waQuery.error} />
            <button type="button" className="btn" onClick={() => waQuery.refetch()}>
              Try again
            </button>
          </div>
        ) : !wa ? (
          <div className="panel-b">
            <Spinner label="Loading WhatsApp" />
          </div>
        ) : (
          <Connection wa={wa} timeZone={timeZone} />
        )}
      </div>
      {wa?.setUp && wa.state === 'connected' && <AlertGroup wa={wa} />}
      {wa?.setUp && <Alerts wa={wa} timeZone={timeZone} />}
    </div>
  );
}

function Connection({ wa, timeZone }: { wa: WhatsAppDto; timeZone: string | undefined }) {
  if (!wa.setUp)
    return (
      <Empty
        title="WhatsApp isn’t set up on this server."
        description="Whoever runs the server installs WhatsApp’s sender (pallet-wa) first, following its guide: apps/wa/README.md."
      />
    );
  if (!wa.reachable)
    return (
      <div className={`callout ${s.body}`}>
        <span className="ic">
          <Icon name="alert" />
        </span>
        <div className={s.calloutText}>
          <b>WhatsApp’s sender isn’t answering.</b>
          <span>This page asks again every 15 seconds. If it stays like this, whoever runs the server checks it: deploy.sh --wa-status.</span>
        </div>
      </div>
    );
  if (wa.state === 'pairing')
    return (
      <div className={s.pair}>
        {wa.qrDataUrl ? <img className={s.qr} src={wa.qrDataUrl} alt="WhatsApp pairing code" width={264} height={264} /> : <Spinner label="Waiting for a pairing code" />}
        <div className={s.steps}>
          <b>Link the company’s WhatsApp number</b>
          <ol>
            <li>On the company’s WhatsApp phone, open Settings → Linked devices → Link a device.</li>
            <li>Scan this code. It changes about every 20 seconds; this page keeps up.</li>
          </ol>
          <small>Use the company’s own number, on a phone that stays with the business. Never a personal number.</small>
        </div>
      </div>
    );
  if (wa.state === 'connected')
    return (
      <div className={s.body}>
        <p className={s.line}>
          Connected as <b>+{wa.connectedAs}</b>
          {wa.connectedAt && <span className="sub"> since {zonedDateTime(wa.connectedAt, timeZone)}</span>}
        </p>
        <p className={s.line}>
          Answering staff questions: {wa.inbound === 'api' ? 'on' : 'off (whoever runs the server turns it on)'}
        </p>
        <p className={s.line}>
          Staff link their own number from <Link className="link" to="/office/whatsapp">Link WhatsApp</Link>. The bot’s number shown to them is set on{' '}
          <Link className="link" to="/office/assistant?tab=number">Office → Assistant</Link>.
        </p>
        {wa.assistantPhone && wa.connectedAs && wa.assistantPhone !== wa.connectedAs && (
          <div className="callout">
            <span className="ic">
              <Icon name="alert" />
            </span>
            <span className={s.calloutText}>
              {`Office → Assistant shows the bot’s number as +${wa.assistantPhone}, but WhatsApp is connected as +${wa.connectedAs}. Staff send their link codes to the number Office → Assistant shows.`}
            </span>
          </div>
        )}
      </div>
    );
  if (wa.state === 'reconnecting')
    return <p className={`${s.body} ${s.line}`}>Reconnecting to WhatsApp. The link is kept; this usually takes a few seconds.</p>;
  // WhatsApp revoked the session (401), or pallet-wa was told to log out: it has cleared the session
  // and pairs again.
  if (wa.state === 'logged_out')
    return <p className={`${s.body} ${s.line}`}>This device was unlinked from WhatsApp. A new pairing code appears here in a moment.</p>;
  // `unknown`: starting, or unable to start. The session is intact either way.
  return (
    <p className={`${s.body} ${s.line}`}>
      WhatsApp is starting up or not answering. If this lasts more than a minute, the operator should check pallet-wa (see the WhatsApp README). Don’t unlink the phone.
    </p>
  );
}

/**
 * The group alerts go to. Once one is chosen, only it is shown: the number is in many groups that
 * are its owner's business. The whole list comes only with the chooser: while none is chosen, or
 * after an admin's "Change group…" and its confirm; closing the chooser forgets it. A choice applies
 * from the next alert, unless the server's operator limits alerts to other groups.
 */
function AlertGroup({ wa }: { wa: WhatsAppDto }) {
  const qc = useQueryClient();
  const saved = wa.alertGroup?.jid ?? null;
  const [changing, setChanging] = useState(false);
  const [asking, setAsking] = useState(false);
  const choosing = saved === null || changing;
  const chosenQuery = useWhatsAppGroups(!choosing);
  const chooserQuery = useWhatsAppGroupChooser(choosing);
  const [choice, setChoice] = useState<string | null>(saved);
  const [copied, setCopied] = useState(false);
  useEffect(() => setChoice(saved), [saved]);
  // Closed, the list is forgotten: dropped once its query is off, so nothing asks for it again.
  useEffect(() => {
    if (!choosing) qc.removeQueries({ queryKey: qk.whatsappGroupChooser });
  }, [choosing, qc]);

  const closeChooser = () => {
    setChanging(false);
    setChoice(saved);
  };
  const save = useMutation({
    mutationFn: (jid: string | null) => api.put<WhatsAppDto>('/whatsapp/alert-group', { jid }),
    onSuccess: (dto) => {
      setChanging(false);
      qc.setQueryData(qk.whatsapp, dto);
      void qc.invalidateQueries({ queryKey: qk.whatsappGroups, exact: true });
    },
  });

  async function copy(jid: string) {
    try {
      await navigator.clipboard.writeText(jid);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const group = wa.alertGroup;
  const chosen = chosenQuery.data?.find((g) => g.jid === saved);
  const groups: WhatsAppGroupDto[] = chooserQuery.data ?? [];
  return (
    <div className="panel">
      <div className="panel-h">
        <h2>Alert group</h2>
        <span className="grow" />
        <span className="sub">Where a count with missing boxes, or a transfer that arrived short, is reported</span>
      </div>
      <div className={s.body}>
        {!choosing && group && (
          <>
            {group.allowed !== false && (
              <p className={s.line}>
                {`Alerts go to ${group.name}.`}
                {chosen && chosen.participantCount !== null && (
                  <span className="sub">{` ${chosen.participantCount} ${chosen.participantCount === 1 ? 'member' : 'members'}`}</span>
                )}
              </p>
            )}
            {chosenQuery.isLoadingError && <ErrorNote error={chosenQuery.error} />}
            <div className={s.actions}>
              <button type="button" className="btn" onClick={() => setAsking(true)}>
                Change group…
              </button>
            </div>
          </>
        )}
        {choosing && (
          <>
            {chooserQuery.isLoadingError ? (
              <ErrorNote error={chooserQuery.error} />
            ) : !chooserQuery.data ? (
              <Spinner label="Loading the groups" />
            ) : (
              <div role="radiogroup" aria-label="Alert group" className={s.groups}>
                <label className={s.group}>
                  <input type="radio" name="alert-group" checked={choice === null} onChange={() => setChoice(null)} />
                  <span className={s.groupName}>No alerts</span>
                </label>
                {groups.map((g) => (
                  <label key={g.jid} className={s.group}>
                    <input type="radio" name="alert-group" checked={choice === g.jid} onChange={() => setChoice(g.jid)} />
                    <span className={s.groupName}>{g.name ?? g.jid}</span>
                    {g.participantCount !== null && <span className="sub">{`${g.participantCount} ${g.participantCount === 1 ? 'member' : 'members'}`}</span>}
                    {!g.allowed && <Chip>Outside the server’s limit</Chip>}
                  </label>
                ))}
              </div>
            )}
            <div className={s.actions}>
              <button type="button" className="btn primary" disabled={choice === saved || save.isPending} onClick={() => save.mutate(choice)}>
                Save alert group
              </button>
              {saved !== null && (
                <button type="button" className="btn" onClick={closeChooser}>
                  Cancel
                </button>
              )}
              <ErrorNote error={save.error} />
            </div>
          </>
        )}
        {!choosing && group && group.allowed === false && (
          <div className="callout">
            <span className="ic">
              <Icon name="alert" />
            </span>
            <div className={s.calloutText}>
              <b>Your server limits alerts to other groups; ask your operator.</b>
              <span>{`No alert is sent to ${group.name} until then.`}</span>
              <span>
                <button type="button" className="btn sm" onClick={() => void copy(group.jid)}>
                  <Icon name={copied ? 'check' : 'link'} />
                  Copy group ID
                </button>
              </span>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={asking}
        onOpenChange={(open) => setAsking(open)}
        title="Show all of this number’s groups?"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setAsking(false);
                setChanging(true);
              }}
            >
              Show the groups
            </button>
          </>
        }
      >
        <p className={s.line}>Every group this WhatsApp number is in is listed, private ones too. Only admins see the list, and it’s hidden again when you close it.</p>
      </Modal>
    </div>
  );
}

/** What was sent, what waits, and why the last one could not go. No figures: see the API. */
function Alerts({ wa, timeZone }: { wa: WhatsAppDto; timeZone: string | undefined }) {
  return (
    <section className="panel" aria-label="Group alerts">
      <div className="panel-h">
        <h2>Group alerts</h2>
        <span className="grow" />
        {wa.alertsWaiting > 0 && <Chip tone="warn">{`${wa.alertsWaiting} ${wa.alertsWaiting === 1 ? 'alert' : 'alerts'} waiting`}</Chip>}
      </div>
      <div className={s.body}>
        {wa.lastAlertError && (
          <p role="alert" className="error-note">
            {wa.lastAlertError}
          </p>
        )}
        {wa.alertsWaiting > 0 && (
          <p className={s.line}>An alert waits while a count is open at a site it names, and goes within a minute of that count ending.</p>
        )}
        {wa.recentAlerts.length === 0 ? (
          <p className={s.line}>
            {wa.alertsWaiting > 0
              ? 'None sent yet.'
              : 'No alerts yet. When a count finds missing boxes, or a transfer arrives short, the alert group is told.'}
          </p>
        ) : (
          <ul className={s.alerts}>
            {wa.recentAlerts.map((a) => (
              <li key={`${a.at}-${a.text}`}>
                <span className="sub">{zonedDateTime(a.at, timeZone)}</span>
                <span>{a.text}</span>
                <Chip tone={a.outcome === 'sent' ? 'ok' : 'warn'}>{a.outcome === 'sent' ? 'Sent' : 'Dropped: not sent within a day'}</Chip>
              </li>
            ))}
          </ul>
        )}
        <small className={s.never}>
          Alerts carry no figures, products or box codes: a group can include staff who count. A lead reads the details in Office.
        </small>
      </div>
    </section>
  );
}
